import {
  Box, Button, Collapse, Grid, Paper, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography
} from "@mui/material";
import { useShow } from "@refinedev/core";
import {
  NumberField, Show, TextFieldComponent as TextField
} from "@refinedev/mui";
import { config } from "config";
import { useEffect, useState } from "react";
import { dataProvider } from "../../rest-data-provider";

export const ScannerConfigChangesShow = () => {
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;
  const [showRawJson, setShowRawJson] = useState(false);
  const record = data?.data;
  const scannerUrl = config().server.stock;
  const [relatedData, setRelatedData] = useState<any[]>([]);
  const toggleRawJson = () => {
    setShowRawJson(!showRawJson);
  };
  useEffect(() => {
    dataProvider(scannerUrl)
      .getMany({
        resource: "scanner-config-changes",
        ids: [record?.widget_id],
      })
      .then((res) => {
        setRelatedData(res.data.filter((item: any) => item.id !== record?.id));
      });
  }, [record?.id, record?.widget_id, scannerUrl]);
  return (
    <Show
      isLoading={isLoading}
      title={
        <Typography variant="h5">
          Scanner Configuration Change Details
        </Typography>
      }
    >
      {/* Change Overview */}
      <Paper sx={{ p: 3, mb: 4 }} elevation={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Change Information
            </Typography>
            <Stack spacing={1.5}>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  ID:
                </Typography>
                <NumberField value={record?.id ?? ""} />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Date:
                </Typography>
                <TextField
                  value={new Date(record?.ts).toLocaleString() ?? ""}
                />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Type:
                </Typography>
                <TextField value={record?.change_type} />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Operator:
                </Typography>
                <TextField value={record?.op ? record?.op.match(/<(.+?)>/)?.[1] ?? record?.op : ""} />
              </Box>
            </Stack>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" fontWeight="bold" mb={2}>
              Object Information
            </Typography>
            <Stack spacing={1.5}>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Name:
                </Typography>
                <TextField value={record?.obj_name} />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  ID:
                </Typography>
                <TextField value={record?.obj_id ?? ""} />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Type:
                </Typography>
                <TextField value={record?.obj_type} />
              </Box>
              <Box display="flex">
                <Typography width="8rem" color="text.secondary">
                  Widget ID:
                </Typography>
                <TextField value={record?.widget_id ?? ""} />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Configuration Comparison */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Configuration Details
      </Typography>
      <Grid container spacing={4} mb={4}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined">
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'error.dark' : '#FFEBEE',
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography fontWeight="medium" color={(theme) => theme.palette.mode === 'dark' ? 'error.contrastText' : 'error.dark'}>
                Before Change
              </Typography>
            </Box>
            <Box p={2}>
              <Typography
                component="pre"
                fontFamily="monospace"
                fontSize="0.875rem"
                whiteSpace="pre-wrap"
              >
                {record?.friendly_before?.join("\n") ?? "Not Available"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined">
            <Box
              sx={{
                bgcolor: (theme) => theme.palette.mode === 'dark' ? 'success.dark' : '#E8F5E9',
                px: 2,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography fontWeight="medium" color={(theme) => theme.palette.mode === 'dark' ? 'success.contrastText' : 'success.dark'}>
                After Change
              </Typography>
            </Box>
            <Box p={2}>
              <Typography
                component="pre"
                fontFamily="monospace"
                fontSize="0.875rem"
                whiteSpace="pre-wrap"
              >
                {record?.friendly_after?.join("\n") ?? "Not Available"}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Raw JSON Data */}
      <Box mb={4}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight="bold">
            Raw Data
          </Typography>
          <Button color="primary" onClick={toggleRawJson}>
            {showRawJson ? "Hide Raw JSON" : "Show Raw JSON"}
          </Button>
        </Box>
        <Collapse in={showRawJson}>
          <Paper sx={{ 
            p: 2, 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.100',
            border: 1,
            borderColor: 'divider'
          }}>
            <Typography
              component="pre"
              sx={{
                fontSize: "0.875rem",
                overflow: "auto",
                maxHeight: "300px",
                fontFamily: "monospace",
                color: (theme) => theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary',
              }}
            >
              {JSON.stringify(record, null, 2)}
            </Typography>
          </Paper>
        </Collapse>
      </Box>

      {/* Related Changes */}
      <Typography variant="h6" fontWeight="bold" mb={2}>
        Related Changes
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead sx={{ 
            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : 'grey.50',
            '& .MuiTableCell-head': {
              color: (theme) => theme.palette.mode === 'dark' ? 'text.primary' : 'text.primary',
              fontWeight: 'bold'
            }
          }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {relatedData.length > 0 ? (
              relatedData.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.id}</TableCell>
                  <TableCell>{new Date(item.ts).toLocaleString()}</TableCell>
                  <TableCell>{item.change_type}</TableCell>
                  <TableCell>{item.obj_name ? item.obj_name.split(":")[1]?.trim() : ""}</TableCell>
                  <TableCell>
                    <Button 
                      color="primary" 
                      onClick={() => {
                        window.location.href = `/scannerConfigChanges/show/${item.id}`;
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No related changes
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Show>
  );
};
